/**
 * Healthcare disciplines, service/visit types and compensation rates: the UI types.
 *
 * Vocabulary (docs/healthcare-discipline-compensation-contract.md, section 0). Never combine these:
 *   Discipline      the worker's PROFESSIONAL CLASSIFICATION (Registered Nurse, Physical Therapist).
 *   Service type    the WORK PERFORMED (Start of Care, Evaluation, Night Shift). SOC, ROC, EOC, FU, IV are service types, never disciplines.
 *   Pay basis       HOW THE WORKER IS PAID (Hourly, Per Visit, Per Shift, Daily, Salary, Per Unit/Case and, from universal compensation phase B, Per Mile, Per Trip,
 *                   Per Load, Per Class, Per Session, Per Event, Per Transaction and Fixed commission per item).
 *   Reimbursement   what a payer pays the AGENCY. It is not employee pay and never sets a pay rate.
 *
 * Wire shapes are normalised in wireMapping.ts, the only place the wire and these types meet.
 */

// ---- pay basis ----

export type PayBasis =
  | 'Hourly' | 'PerVisit' | 'PerShift' | 'Daily' | 'Salary' | 'PerUnit'
  | 'PerMile' | 'PerTrip' | 'PerLoad' | 'PerClass' | 'PerSession' | 'PerEvent' | 'PerTransaction' | 'FixedCommission';

/** Every basis this build knows, in the order they are offered. */
export const PAY_BASES: readonly PayBasis[] = [
  'Hourly', 'PerVisit', 'PerShift', 'Daily', 'Salary', 'PerUnit',
  'PerMile', 'PerTrip', 'PerLoad', 'PerClass', 'PerSession', 'PerEvent', 'PerTransaction', 'FixedCommission',
];

/** The six pay types an employee's BASE pay assignment can use (Compensation stores them as 1, 6, 7, 8, 2, 9). A basis added in phase B is a service-rate basis only. */
export const BASE_PAY_BASES: readonly PayBasis[] = ['Hourly', 'PerVisit', 'PerShift', 'Daily', 'Salary', 'PerUnit'];

/** The five bases that existed before phase B. Every server accepts them, so they are always offered. */
export const ORIGINAL_SERVICE_PAY_BASES: readonly PayBasis[] = ['Hourly', 'PerVisit', 'PerShift', 'Daily', 'PerUnit'];

/** The bases a service rate or a pay-run service line can use: Salary stays on the employee's base assignment. A basis added in phase B is offered only
 *  when Payroll Calculation says it can price it (see `offeredServicePayBases`). */
export const SERVICE_PAY_BASES: readonly PayBasis[] = PAY_BASES.filter((b) => b !== 'Salary');

/** The bases a screen offers: always the original five, plus each newer basis the server reports it can price. `supported` is null when the list could not be read. */
export function offeredServicePayBases(supported: readonly string[] | null): readonly PayBasis[] {
  if (supported === null) return ORIGINAL_SERVICE_PAY_BASES;
  return SERVICE_PAY_BASES.filter((b) => ORIGINAL_SERVICE_PAY_BASES.includes(b) || supported.includes(b));
}

export const payBasisLabels: Record<PayBasis, string> = {
  Hourly: 'Hourly',
  PerVisit: 'Per Visit',
  PerShift: 'Per Shift',
  Daily: 'Daily',
  Salary: 'Salary',
  PerUnit: 'Per Unit/Case',
  PerMile: 'Per Mile',
  PerTrip: 'Per Trip',
  PerLoad: 'Per Load',
  PerClass: 'Per Class',
  PerSession: 'Per Session',
  PerEvent: 'Per Event',
  PerTransaction: 'Per Transaction',
  FixedCommission: 'Fixed commission per item',
};

/** The Rate field's label for each basis (contract section 4). */
export const rateLabels: Record<PayBasis, string> = {
  Hourly: 'Hourly Rate',
  PerVisit: 'Rate per Visit',
  PerShift: 'Rate per Shift',
  Daily: 'Daily Rate',
  Salary: 'Salary',
  PerUnit: 'Rate per Unit/Case',
  PerMile: 'Rate per Mile',
  PerTrip: 'Rate per Trip',
  PerLoad: 'Rate per Load',
  PerClass: 'Rate per Class',
  PerSession: 'Rate per Session',
  PerEvent: 'Rate per Event',
  PerTransaction: 'Rate per Transaction',
  FixedCommission: 'Fixed Commission per Item',
};

/** One plain sentence per basis, shown under the Rate field. */
export const rateHelp: Record<PayBasis, string> = {
  Hourly: 'The amount paid for each hour worked.',
  PerVisit: 'The amount paid for each visit completed.',
  PerShift: 'The amount paid for each shift worked.',
  Daily: 'The amount paid for each day worked.',
  Salary: 'The fixed amount paid for the year, spread over the pay periods.',
  PerUnit: 'The amount paid for each unit or case completed.',
  PerMile: 'The amount paid for each mile driven.',
  PerTrip: 'The amount paid for each trip completed.',
  PerLoad: 'The amount paid for each load delivered.',
  PerClass: 'The amount paid for each class taught.',
  PerSession: 'The amount paid for each session completed.',
  PerEvent: 'The amount paid for each event worked.',
  PerTransaction: 'The amount paid for each transaction completed.',
  FixedCommission: 'A flat amount paid for each qualifying item. It is not a percentage of a sale, and no sales or tip amount is used.',
};

/** The unit a quantity is counted in, for a line like "3 visits". */
export const quantityUnits: Record<PayBasis, { singular: string; plural: string }> = {
  Hourly: { singular: 'hour', plural: 'hours' },
  PerVisit: { singular: 'visit', plural: 'visits' },
  PerShift: { singular: 'shift', plural: 'shifts' },
  Daily: { singular: 'day', plural: 'days' },
  Salary: { singular: 'period', plural: 'periods' },
  PerUnit: { singular: 'unit or case', plural: 'units or cases' },
  PerMile: { singular: 'mile', plural: 'miles' },
  PerTrip: { singular: 'trip', plural: 'trips' },
  PerLoad: { singular: 'load', plural: 'loads' },
  PerClass: { singular: 'class', plural: 'classes' },
  PerSession: { singular: 'session', plural: 'sessions' },
  PerEvent: { singular: 'event', plural: 'events' },
  PerTransaction: { singular: 'transaction', plural: 'transactions' },
  FixedCommission: { singular: 'item', plural: 'items' },
};

export function isPayBasis(value: unknown): value is PayBasis {
  return typeof value === 'string' && (PAY_BASES as readonly string[]).includes(value);
}

// ---- wording that must read the same everywhere ----

/** The compliance boundary (contract section 1), as one plain sentence. */
export const REIMBURSEMENT_SEPARATE_NOTE =
  'Reimbursement from Medicare or any payer is separate from employee pay. It is paid to your company, and it does not set what you pay your employees.';

/** The same boundary in one short sentence, for a help line under a field. */
export const REIMBURSEMENT_SEPARATE_SHORT = 'Reimbursement from Medicare or any payer is separate from employee pay.';

/** Plain-language definitions shown wherever the four terms meet (Add Employee, Edit Employee). */
export const TERM_HELP = {
  discipline: 'The worker’s professional classification, for example Registered Nurse or Electrician. In many healthcare organizations it is called a discipline. It is not the work performed on a visit or shift.',
  serviceType: 'The work performed, for example Start of Care, Evaluation or Night Shift. You choose it when you add rates or pay-run lines, not on the employee.',
  payType: 'How the worker is paid: hourly, per visit, per shift, daily, salary, or per unit or case. Per-visit, per-shift, daily and per-unit pay is worked out from quantities entered on a payroll line.',
  rate: 'The amount paid for one hour, visit, shift, day or unit (or the yearly salary), according to the Pay Type.',
} as const;

/** The label any future reimbursement reference must carry. Phase 1 shows no such information; the constant exists for later use. */
export const INFORMATIONAL_REIMBURSEMENT_LABEL = 'Informational reimbursement reference - not employee compensation.';

/** Shown wherever synthetic mock data is shown. */
export const SYNTHETIC_RATES_NOTICE_TITLE = 'Synthetic example rates';
export const SYNTHETIC_RATES_NOTICE =
  'These are SYNTHETIC EXAMPLE RATES for demonstration. They are not market rates, not Medicare rates and not legally required rates. Enter your own rates from your compensation agreements.';
/** Every synthetic rate row's description starts with this, so a row is recognisable as an example. */
export const SYNTHETIC_RATE_PREFIX = 'SYNTHETIC EXAMPLE';

export const WORKED_HOURS_HELP =
  'Worked hours are kept on the line so a person can review minimum-wage and overtime compliance. They do not change the amount, and EPN does not apply overtime to a per-visit, per-shift or daily line.';

// ---- industries ----

export type IndustryCode =
  | 'HomeHealth' | 'Hospice' | 'Hospital' | 'SkilledNursingFacility'
  | 'ClinicOrPhysicianPractice' | 'BehavioralHealth' | 'OtherHealthcare' | 'GeneralNonHealthcare';

export interface IndustryOption {
  code: string;
  label: string;
}

/** Used only when the server's own options cannot be read; the server's list wins. */
export const FALLBACK_INDUSTRY_OPTIONS: readonly IndustryOption[] = [
  { code: 'HomeHealth', label: 'Home health' },
  { code: 'Hospice', label: 'Hospice' },
  { code: 'Hospital', label: 'Hospital' },
  { code: 'SkilledNursingFacility', label: 'Skilled nursing facility' },
  { code: 'ClinicOrPhysicianPractice', label: 'Clinic or physician practice' },
  { code: 'BehavioralHealth', label: 'Behavioral health' },
  { code: 'OtherHealthcare', label: 'Other healthcare' },
  { code: 'GeneralNonHealthcare', label: 'General (non-healthcare)' },
];

export interface CompanyIndustry {
  /** Null until the company chooses one. */
  industry: string | null;
  options: IndustryOption[];
  updatedAtUtc: string | null;
}

// ---- disciplines (Employees) ----

export interface Discipline {
  id: string;
  code: string;
  name: string;
  description: string;
  industries: string[];
  /** A system default (read-only to companies) as opposed to the company's own. */
  isSystem: boolean;
  isActive: boolean;
  concurrencyVersion: number;
}

export interface ListDisciplinesParams {
  industry?: string | undefined;
  search?: string | undefined;
  includeInactive?: boolean | undefined;
}

export interface DisciplineInput {
  name: string;
  code: string;
  description: string;
  industries: string[];
  isActive: boolean;
}

export interface DisciplineAssignment {
  id: string;
  disciplineId: string;
  disciplineCode: string;
  disciplineName: string;
  isPrimary: boolean;
  /** yyyy-MM-dd */
  effectiveFrom: string;
  /** null = current */
  effectiveTo: string | null;
  reason: string | null;
}

/** One work type an employee may be paid for (the server's snapshot of its code and name; universal compensation phase C-1). */
export interface AllowedWorkType {
  serviceTypeId: string;
  code: string;
  name: string;
}

/**
 * The work types an employee may be paid for. `isRestricted` false (an empty list) means NO limit: the employee may be paid for any work type, as before the list existed.
 * Nothing ever fills the list in automatically. With a list, a pay-run line must name one of these work types.
 */
export interface AllowedWorkTypes {
  employeeId: string;
  isRestricted: boolean;
  items: AllowedWorkType[];
}

/** The most work types one employee's list can hold (the server refuses more). */
export const MAX_ALLOWED_WORK_TYPES = 100;

export interface CreateDisciplineAssignmentInput {
  disciplineId: string;
  isPrimary: boolean;
  effectiveFromDate?: string | undefined;
  reason?: string | undefined;
}

export interface DisciplineCapabilities {
  canManageDisciplines: boolean;
  canAssignDisciplines: boolean;
}

// ---- service types (PayrollConfig) ----

export interface ServiceType {
  id: string;
  code: string;
  name: string;
  description: string;
  industries: string[];
  isSystem: boolean;
  isActive: boolean;
  /** A hint only; never applied for the person. */
  suggestedPayBasis: PayBasis | null;
  concurrencyVersion: number;
}

export interface ListServiceTypesParams {
  industry?: string | undefined;
  search?: string | undefined;
  includeInactive?: boolean | undefined;
}

export interface ServiceTypeInput {
  name: string;
  code: string;
  description: string;
  industries: string[];
  suggestedPayBasis: PayBasis | null;
  isActive: boolean;
}

export interface ServiceTypeCapabilities {
  canManageServiceTypes: boolean;
  canManageCompanyRates: boolean;
}

// ---- rates (PayrollConfig company rates, Compensation employee rates) ----

export type DifferentialKind = 'None' | 'Amount' | 'Percent';

export const differentialLabels: Record<DifferentialKind, string> = {
  None: 'No differential',
  Amount: 'Add a fixed amount',
  Percent: 'Add a percentage of the rate',
};

export interface ServiceRate {
  id: string;
  /** Present on an employee rate; absent on a company rate. */
  employeeId?: string | undefined;
  /** The worker category (discipline). null = a company rate for ANY worker category (universal compensation phase B); an employee rate always names one. */
  disciplineId: string | null;
  /** A best-effort display snapshot stored when the row was created. */
  disciplineName: string | null;
  /** null = any service type */
  serviceTypeId: string | null;
  serviceTypeName: string | null;
  /** null = all locations */
  workLocationId: string | null;
  payBasis: PayBasis;
  /** Dollars, exactly as the server sent it (up to 4 decimals). Never used in arithmetic here. */
  rate: number;
  currency: string;
  differentialKind: DifferentialKind;
  differentialValue: number | null;
  description: string;
  effectiveFrom: string;
  /** The first day the rate NO LONGER applies (exclusive). An end equal to the start voids a row entered by mistake. */
  effectiveTo: string | null;
  /** The service's own verdict that the row is in effect today. */
  isActive: boolean;
  concurrencyVersion: number;
  /** True for a company rate that applies to any worker category (the row names none). */
  appliesToAnyWorkerCategory: boolean;
}

export interface ListServiceRatesParams {
  disciplineId?: string | undefined;
  serviceTypeId?: string | undefined;
  workLocationId?: string | undefined;
  payBasis?: PayBasis | undefined;
  asOf?: string | undefined;
  includeEnded?: boolean | undefined;
}

export interface CreateServiceRateInput {
  /** The worker category, or null together with `appliesToAnyWorkerCategory` for a company rate that applies to any category. */
  disciplineId: string | null;
  /** Set only on a company rate for any worker category (the server requires this explicit choice; a missing category alone is refused). */
  appliesToAnyWorkerCategory?: boolean | undefined;
  serviceTypeId: string | null;
  workLocationId: string | null;
  payBasis: PayBasis;
  /** Integer cents (2 decimals); the mapping layer writes it to the wire without floating-point arithmetic. */
  rateCents: number;
  differentialKind: DifferentialKind;
  /** Integer cents for Amount, integer hundredths of a percent for Percent, null for None. */
  differentialUnits: number | null;
  description: string;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export interface EmployeeRateCapabilities {
  canManageEmployeeRates: boolean;
}

// ---- stable problem codes (never matched on message text) ----

export const HdcErrorCode = {
  DisciplineNameExists: 'DISCIPLINE_NAME_EXISTS',
  DisciplineCodeExists: 'DISCIPLINE_CODE_EXISTS',
  DisciplineInvalid: 'DISCIPLINE_INVALID',
  DisciplineIsSystem: 'DISCIPLINE_IS_SYSTEM',
  ServiceTypeNameExists: 'SERVICE_TYPE_NAME_EXISTS',
  ServiceTypeCodeExists: 'SERVICE_TYPE_CODE_EXISTS',
  ServiceTypeInvalid: 'SERVICE_TYPE_INVALID',
  ServiceTypeIsSystem: 'SERVICE_TYPE_IS_SYSTEM',
  ConcurrencyConflict: 'CONCURRENCY_CONFLICT',
  DisciplineNotFound: 'DISCIPLINE_NOT_FOUND',
  DisciplineInactive: 'DISCIPLINE_INACTIVE',
  AssignmentConflict: 'ASSIGNMENT_CONFLICT',
  AssignmentAlreadyEnded: 'ASSIGNMENT_ALREADY_ENDED',
  AssignmentNotFound: 'ASSIGNMENT_NOT_FOUND',
  AssignmentDateInvalid: 'ASSIGNMENT_DATE_INVALID',
  RateInvalid: 'RATE_INVALID',
  PayBasisNotSupported: 'PAY_BASIS_NOT_SUPPORTED',
  EmployeeRateInvalid: 'EMPLOYEE_RATE_INVALID',
  RateAlreadyEnded: 'RATE_ALREADY_ENDED',
  ServiceRateNotFound: 'SERVICE_RATE_NOT_FOUND',
  EmployeeRateNotFound: 'EMPLOYEE_RATE_NOT_FOUND',
  ServiceTypeNotFound: 'SERVICE_TYPE_NOT_FOUND',
  ServiceTypeInactive: 'SERVICE_TYPE_INACTIVE',
  AllowedWorkTypesInvalid: 'ALLOWED_WORK_TYPES_INVALID',
  AllowedWorkTypesConflict: 'ALLOWED_WORK_TYPES_CONFLICT',
  WorkLocationNotFound: 'WORK_LOCATION_NOT_FOUND',
  DependencyUnavailable: 'DEPENDENCY_UNAVAILABLE',
  RateOverlap: 'RATE_OVERLAP',
  RateNotFound: 'RATE_NOT_FOUND',
  RateAmbiguous: 'RATE_AMBIGUOUS',
  DisciplineNotAssigned: 'DISCIPLINE_NOT_ASSIGNED',
  WorkTypeNotAllowed: 'WORK_TYPE_NOT_ALLOWED',
  DoublePaymentBlocked: 'DOUBLE_PAYMENT_BLOCKED',
  RateNotClientSupplied: 'RATE_NOT_CLIENT_SUPPLIED',
  ServiceLineInvalid: 'SERVICE_LINE_INVALID',
} as const;

/** The problem codes of a pay-run service line (Custom Pay Run / Payroll Calculation). */
export const SERVICE_LINE_ERROR_CODES: readonly string[] = [
  HdcErrorCode.RateNotFound, HdcErrorCode.RateAmbiguous, HdcErrorCode.DisciplineNotAssigned, HdcErrorCode.WorkTypeNotAllowed,
  HdcErrorCode.DoublePaymentBlocked, HdcErrorCode.RateNotClientSupplied, HdcErrorCode.ServiceLineInvalid,
];

// ---- the pay-run service line and the server's per-line record ----

/** Where the resolver took the rate from (contract section 8). */
export type RateSource = 'EmployeeServiceRate' | 'CompanyLocationRate' | 'CompanyDefaultRate' | 'EmployeeBaseRate' | 'AdHoc' | 'BaseAssignment';

export const rateSourceLabels: Record<RateSource, string> = {
  EmployeeServiceRate: "This employee's own service rate",
  CompanyLocationRate: "Your company's rate for this location",
  CompanyDefaultRate: "Your company's default rate",
  EmployeeBaseRate: "The employee's base rate",
  AdHoc: 'Entered on the line',
  BaseAssignment: "The employee's base pay",
};

/** What a person enters on an earning line that is priced from a rate: never a rate, never an amount. */
export interface ServiceLine {
  disciplineId: string;
  serviceTypeId: string | null;
  payBasis: PayBasis;
  /** At most 4 decimals, > 0 and <= 10,000. Held as a number; built from the typed text without floating-point arithmetic. */
  quantity: number;
  workedHours: number | null;
  /** yyyy-MM-dd; the server defaults it to the period end date. */
  serviceDate: string | null;
  workLocationId: string | null;
  additionalEarningAuthorized: boolean;
  additionalEarningReason: string | null;
  /** Snapshots the server stored (response only). */
  disciplineCode?: string | null | undefined;
  disciplineName?: string | null | undefined;
  serviceTypeCode?: string | null | undefined;
  serviceTypeName?: string | null | undefined;
}

/** One earning line as the calculation priced it (`earningDetails[]`). */
export interface EarningDetail {
  label: string;
  amount: number;
  payBasis: PayBasis | null;
  quantity: number | null;
  rate: number | null;
  differentialKind: DifferentialKind | null;
  differentialValue: number | null;
  effectiveRate: number | null;
  rateSource: RateSource | null;
  rateRuleId: string | null;
  /** A plain sentence the server wrote. */
  explanation: string;
  disciplineId: string | null;
  disciplineCode: string | null;
  disciplineName: string | null;
  serviceTypeId: string | null;
  serviceTypeCode: string | null;
  serviceTypeName: string | null;
  workedHours: number | null;
  serviceDate: string | null;
  workLocationId: string | null;
  additionalEarningAuthorized: boolean | null;
  additionalEarningReason: string | null;
}

// ---- the company industry profile and the NAICS reference (PayrollConfig; docs/universal-industry-compensation.md) ----
//
// The industry only suggests starting worker categories, work types and pay bases. It never sets pay, restricts who the company may pay,
// selects a legal classification or decides overtime exemption.

/** One of the 20 broad NAICS sectors (`31-33`, `44-45` and `48-49` are ranges). */
export interface NaicsSector {
  code: string;
  title: string;
}

/** A detailed (six-digit) NAICS industry. */
export interface NaicsIndustry {
  code: string;
  title: string;
  sectorCode: string;
  sectorTitle: string;
}

export interface NaicsIndustrySearch {
  version: string;
  items: NaicsIndustry[];
  /** True when more rows matched than were returned: refine the search. */
  hasMore: boolean;
}

export type IndustryKind = 'Chosen' | 'NotSure' | 'Custom';

/** One industry answer as the server returns it (titles resolved). */
export interface IndustrySelection {
  kind: IndustryKind;
  sectorCode: string | null;
  sectorTitle: string | null;
  detailCode: string | null;
  detailTitle: string | null;
  customDescription: string | null;
  /** Only for the Health Care and Social Assistance sector. */
  healthcareTemplate: string | null;
  healthcareTemplateLabel: string | null;
}

/** One industry answer as it is sent. */
export interface IndustrySelectionInput {
  kind: IndustryKind;
  sectorCode?: string | undefined;
  detailCode?: string | undefined;
  customDescription?: string | undefined;
  healthcareTemplate?: string | undefined;
}

export interface CompanyIndustryProfile {
  primary: IndustrySelection | null;
  additional: IndustrySelection[];
  naicsVersion: string | null;
  /** True for a company whose original answer was "General (non-healthcare)": EPN did not guess a sector. */
  needsReview: boolean;
  /** True when the primary or any additional industry is Health Care and Social Assistance. */
  isHealthcare: boolean;
  /** The industry tags the pickers list first. Suggestions only. */
  suggestionTags: string[];
  healthcareTemplates: IndustryOption[];
  source: 'Profile' | 'LegacySetting' | 'None';
  /** 0 until a profile is saved; send it back with the next save. */
  concurrencyVersion: number;
  updatedAtUtc: string | null;
}

export interface SetIndustryProfileInput {
  primary: IndustrySelectionInput;
  additional: IndustrySelectionInput[];
  expectedConcurrencyVersion?: number | undefined;
}

/**
 * What a PROPOSED industry would change in the suggestions, from `POST /api/company-industry-profile/preview` (nothing is saved). The tags are exactly the ones a save would
 * carry. An empty list of tags means "no suggestions", which lists every default. Suggestions only: nothing is deleted and no pay changes.
 */
export interface IndustryChangePreview {
  currentSuggestionTags: string[];
  currentIsHealthcare: boolean;
  proposedSuggestionTags: string[];
  proposedIsHealthcare: boolean;
  addedSuggestionTags: string[];
  removedSuggestionTags: string[];
  suggestionsChange: boolean;
  naicsVersion: string | null;
}

/** `Ok`: nothing to do. `Info`: worth knowing, nothing is wrong. `Review`: a person may want to look. None blocks anything or changes pay. */
export type SetupReviewSeverity = 'Ok' | 'Info' | 'Review';

export const setupReviewSeverityLabels: Record<SetupReviewSeverity, string> = { Ok: 'Fine', Info: 'Good to know', Review: 'To look at' };

export interface SetupReviewItem {
  key: string;
  area: string;
  label: string;
  severity: SetupReviewSeverity;
  message: string;
  count: number | null;
  /** At most ten of the `count` things the item is about, sorted. */
  names: string[];
}

/**
 * The compensation setup review from `GET /api/compensation-setup-review` (PayrollConfig only): a READ-ONLY report of how the company's setup reads under the universal model.
 * It changes nothing, rewrites nothing and nothing in it needs to be done for payroll to keep working. `notIncluded` says what it does not look at.
 */
export interface CompensationSetupReview {
  rulesVersion: number;
  /** yyyy-MM-dd, the day the company rates were counted for. */
  asOf: string;
  industry: { source: 'Profile' | 'LegacySetting' | 'None'; needsReview: boolean; isHealthcare: boolean; label: string | null };
  workTypes: { systemActive: number; systemInactive: number; companyActive: number; companyInactive: number };
  companyRates: { active: number; byPayBasis: Array<{ payBasis: string; count: number }>; beyondOriginalFive: number };
  items: SetupReviewItem[];
  summary: { review: number; info: number; ok: number };
  notIncluded: Array<{ area: string; label: string; whereToLook: string }>;
}

/** A default or company-created worker category or work type, as far as the preview needs it. */
export interface PreviewRow {
  id: string;
  name: string;
  isActive: boolean;
  isSystem: boolean;
  industries: readonly string[];
}

export interface ListChanges {
  /** Active EPN defaults that would be listed first and are not now. */
  newlyListed: string[];
  /** Active EPN defaults that are listed first now and would stop being. */
  noLongerListed: string[];
  /** How many active rows are listed first now / would be afterwards (a company's own rows are always listed). */
  listedNow: number;
  listedAfter: number;
}

/**
 * The same rule every list on the Compensation Rates page uses: an EPN default is listed first when it carries one of the suggested tags, or when there are no
 * suggested tags at all (every default is then listed); a company's own row is always listed. Only active rows count. Nothing here deletes or hides a row from anyone
 * who asks for all of them.
 */
export function listChangesFor(rows: readonly PreviewRow[], currentTags: readonly string[], proposedTags: readonly string[]): ListChanges {
  const listed = (row: PreviewRow, tags: readonly string[]) => !row.isSystem || tags.length === 0 || matchesSuggestion(row.industries, tags);
  const active = rows.filter((r) => r.isActive);
  const byName = (a: PreviewRow, b: PreviewRow) => a.name.localeCompare(b.name);
  return {
    newlyListed: active.filter((r) => r.isSystem && !listed(r, currentTags) && listed(r, proposedTags)).sort(byName).map((r) => r.name),
    noLongerListed: active.filter((r) => r.isSystem && listed(r, currentTags) && !listed(r, proposedTags)).sort(byName).map((r) => r.name),
    listedNow: active.filter((r) => listed(r, currentTags)).length,
    listedAfter: active.filter((r) => listed(r, proposedTags)).length,
  };
}

export const MAX_ADDITIONAL_INDUSTRIES = 5;
export const INDUSTRY_DESCRIPTION_MAX = 200;
export const HEALTHCARE_SECTOR_CODE = '62';

/** The guidance shown with the industry selector. */
export const INDUSTRY_HELP =
  'Choose the industry that best describes your company. This only suggests useful worker categories, work types and pay bases. It does not set employee pay or change payroll taxes.';

/** Shown only when the company's industry is Health Care and Social Assistance. */
export const HEALTHCARE_REIMBURSEMENT_NOTE =
  'Medicare, Medicaid and other payer reimbursements are separate from employee compensation. Reimbursement rates do not automatically determine what employees are paid.';

/** The page introduction: one wording for every industry. */
export const COMPENSATION_INTRO =
  'Set the standard ways your company pays workers based on the work they perform. Industry templates provide optional starting suggestions. You can add your own worker categories, work types and rates at any time. These settings do not change an employee’s pay until assigned and used in payroll.';

/** Healthcare-only vocabulary hints (the universal words stay the primary labels). */
export const HEALTHCARE_TERM_HINTS = {
  workerCategory: 'Worker category is called Discipline in many healthcare organizations.',
  workType: 'Work type may be a service or visit type in healthcare.',
} as const;

/** The 20 NAICS sectors by code (official 2022 titles), so a row's tag can be shown in words without another request. */
const NAICS_SECTOR_TITLES: Record<string, string> = {
  '11': 'Agriculture, Forestry, Fishing and Hunting', '21': 'Mining, Quarrying, and Oil and Gas Extraction', '22': 'Utilities', '23': 'Construction',
  '31-33': 'Manufacturing', '42': 'Wholesale Trade', '44-45': 'Retail Trade', '48-49': 'Transportation and Warehousing', '51': 'Information',
  '52': 'Finance and Insurance', '53': 'Real Estate and Rental and Leasing', '54': 'Professional, Scientific, and Technical Services',
  '55': 'Management of Companies and Enterprises', '56': 'Administrative and Support and Waste Management and Remediation Services', '61': 'Educational Services',
  '62': 'Health Care and Social Assistance', '71': 'Arts, Entertainment, and Recreation', '72': 'Accommodation and Food Services',
  '81': 'Other Services (except Public Administration)', '92': 'Public Administration',
};

/** An industry tag in words: a healthcare template ("Hospice") or a NAICS sector ("Naics23" becomes "Construction"). Unknown tags are returned as they are. */
export function industryTagLabel(tag: string): string {
  const healthcare = FALLBACK_INDUSTRY_OPTIONS.find((o) => o.code === tag);
  if (healthcare) return healthcare.label;
  return tag.startsWith('Naics') ? (NAICS_SECTOR_TITLES[tag.slice(5)] ?? tag) : tag;
}

/** True when any of a row's industry tags is one of the tags suggested for the company. */
export function matchesSuggestion(rowTags: readonly string[], suggestionTags: readonly string[]): boolean {
  return rowTags.some((t) => suggestionTags.includes(t));
}

/** The industry tags a company-created worker category or work type carries by default: its primary industry's sector, or none. */
export function defaultTagsFor(profile: CompanyIndustryProfile | null): string[] {
  const sector = profile?.primary?.sectorCode;
  return sector ? [`Naics${sector}`] : [];
}

/** A short plain label for the company's primary industry ("Construction", "Hospice", "Mobile boat repair"), or null. */
export function primaryIndustryLabel(profile: CompanyIndustryProfile | null): string | null {
  const p = profile?.primary;
  if (!p || p.kind === 'NotSure') return null;
  return p.healthcareTemplateLabel ?? p.customDescription ?? p.detailTitle ?? p.sectorTitle;
}
